import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Search, BadgeCheck, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import CRMLayout from '../../components/CRM/CRMLayout';
import { FINANCE_TYPE_OPTIONS } from '../../utils/financeType';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const INDUSTRIES = ['Construction', 'Transport & Logistics', 'Manufacturing', 'Agriculture', 'Healthcare', 'Hospitality', 'Retail', 'Professional Services', 'Mining', 'Other'];
const YEARS_TRADING = ['Less than 1 year', '1-2 years', '2-5 years', '5-10 years', '10+ years'];

export default function NewDeal() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [abnLookupLoading, setAbnLookupLoading] = useState(false);
  const [abnVerified, setAbnVerified] = useState(false);
  const [abnError, setAbnError] = useState(null);
  
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    abn: '',
    business_name: '',
    entity_type: '',
    gst_registered: null,
    years_trading: '',
    industry: '',
    finance_type: 'Asset Finance',
    amount: '',
    term_months: 60,
    balloon_percent: 0,
    asset_description: '',
    is_new_or_used: 'new',
    notes: '',
    source: 'manual'
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleABNLookup = async () => {
    const cleanABN = formData.abn.replace(/\s/g, '');
    if (cleanABN.length !== 11) {
      setAbnError('ABN must be 11 digits');
      return;
    }
    
    setAbnLookupLoading(true);
    setAbnError(null);
    setAbnVerified(false);
    
    try {
      const response = await axios.get(`${API}/abn-lookup/${cleanABN}`);
      const data = response.data;
      
      if (data.is_valid && data.entity_name) {
        setFormData(prev => ({
          ...prev,
          business_name: data.entity_name || prev.business_name,
          entity_type: data.entity_type || '',
          gst_registered: data.gst_registered,
          years_trading: mapYearsTrading(data.years_trading) || prev.years_trading
        }));
        setAbnVerified(true);
        toast.success(`Business verified: ${data.entity_name}`);
      } else {
        setAbnError(data.error_message || 'ABN not found');
      }
    } catch (error) {
      setAbnError('Unable to verify ABN');
    } finally {
      setAbnLookupLoading(false);
    }
  };

  const mapYearsTrading = (years) => {
    const mapping = {
      '0-1': 'Less than 1 year',
      '1-2': '1-2 years',
      '2-5': '2-5 years',
      '5-10': '5-10 years',
      '10+': '10+ years'
    };
    return mapping[years] || null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_name || !formData.finance_type || !formData.amount) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount) || 0,
        term_months: parseInt(formData.term_months) || 60,
        balloon_percent: parseInt(formData.balloon_percent) || 0
      };
      
      const response = await axios.post(`${API}/crm/deals`, payload);
      toast.success('Deal created successfully');
      navigate(`/crm/deals/${response.data.id}`);
    } catch (error) {
      console.error('Error creating deal:', error);
      toast.error('Failed to create deal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CRMLayout>
    <div className="min-h-screen bg-navy-900 pb-20">
      {/* Header */}
      <div className="bg-navy-800 border-b border-white/5">
        <div className="px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/crm/pipeline" className="text-gray-400 hover:text-white">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-heading text-white uppercase">New Deal</h1>
              <p className="text-gray-400 text-sm">Create a new finance deal</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 max-w-3xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Client Details */}
          <div className="bg-navy-800 rounded-xl border border-white/5 p-6">
            <h2 className="text-white font-semibold mb-4">Client Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1">Client Name *</label>
                <input
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => handleChange('client_name', e.target.value)}
                  className="w-full px-4 py-2 bg-navy-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.client_phone}
                  onChange={(e) => handleChange('client_phone', e.target.value)}
                  className="w-full px-4 py-2 bg-navy-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-gray-400 text-sm mb-1">Email</label>
                <input
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => handleChange('client_email', e.target.value)}
                  className="w-full px-4 py-2 bg-navy-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>
          </div>

          {/* Business Details */}
          <div className="bg-navy-800 rounded-xl border border-white/5 p-6">
            <h2 className="text-white font-semibold mb-4">Business Details</h2>
            
            {/* ABN Lookup */}
            <div className="mb-4 p-4 bg-navy-900/50 rounded-lg border border-purple-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Search size={16} className="text-purple-400" />
                <label className="text-gray-300 text-sm">ABN Lookup</label>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.abn}
                  onChange={(e) => {
                    handleChange('abn', e.target.value);
                    setAbnVerified(false);
                    setAbnError(null);
                  }}
                  placeholder="Enter 11-digit ABN"
                  className="flex-1 px-4 py-2 bg-navy-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <Button
                  type="button"
                  onClick={handleABNLookup}
                  disabled={abnLookupLoading || formData.abn.replace(/\s/g, '').length !== 11}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {abnLookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Lookup'}
                </Button>
              </div>
              {abnVerified && (
                <div className="mt-2 flex items-center gap-2 text-green-400 text-sm">
                  <BadgeCheck size={16} />
                  ABN Verified
                  {formData.gst_registered && <span className="bg-green-500/20 px-2 py-0.5 rounded text-xs">GST</span>}
                </div>
              )}
              {abnError && (
                <div className="mt-2 flex items-center gap-2 text-red-400 text-sm">
                  <AlertCircle size={16} />
                  {abnError}
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-gray-400 text-sm mb-1">
                  Business Name
                  {abnVerified && <BadgeCheck size={14} className="inline ml-1 text-green-400" />}
                </label>
                <input
                  type="text"
                  value={formData.business_name}
                  onChange={(e) => handleChange('business_name', e.target.value)}
                  className={`w-full px-4 py-2 bg-navy-900 border rounded-lg text-white focus:outline-none focus:border-purple-500 ${abnVerified ? 'border-green-500/50' : 'border-gray-700'}`}
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Years Trading</label>
                <select
                  value={formData.years_trading}
                  onChange={(e) => handleChange('years_trading', e.target.value)}
                  className="w-full px-4 py-2 bg-navy-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                >
                  <option value="">Select...</option>
                  {YEARS_TRADING.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Industry</label>
                <select
                  value={formData.industry}
                  onChange={(e) => handleChange('industry', e.target.value)}
                  className="w-full px-4 py-2 bg-navy-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                >
                  <option value="">Select...</option>
                  {INDUSTRIES.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Finance Details */}
          <div className="bg-navy-800 rounded-xl border border-white/5 p-6">
            <h2 className="text-white font-semibold mb-4">Finance Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1">Finance Type *</label>
                <select
                  value={formData.finance_type}
                  onChange={(e) => handleChange('finance_type', e.target.value)}
                  className="w-full px-4 py-2 bg-navy-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  required
                >
                  {FINANCE_TYPE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Amount *</label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  placeholder="50000"
                  className="w-full px-4 py-2 bg-navy-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Term (months)</label>
                <select
                  value={formData.term_months}
                  onChange={(e) => handleChange('term_months', e.target.value)}
                  className="w-full px-4 py-2 bg-navy-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                >
                  {[24, 36, 48, 60, 72, 84].map(m => <option key={m} value={m}>{m} months ({m/12} years)</option>)}
                </select>
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Balloon %</label>
                <select
                  value={formData.balloon_percent}
                  onChange={(e) => handleChange('balloon_percent', e.target.value)}
                  className="w-full px-4 py-2 bg-navy-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                >
                  {[0, 10, 20, 30, 40, 50].map(p => <option key={p} value={p}>{p}%</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-gray-400 text-sm mb-1">Asset Description</label>
                <input
                  type="text"
                  value={formData.asset_description}
                  onChange={(e) => handleChange('asset_description', e.target.value)}
                  placeholder="e.g., 2024 Toyota Hilux, Caterpillar Excavator"
                  className="w-full px-4 py-2 bg-navy-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">New or Used</label>
                <select
                  value={formData.is_new_or_used}
                  onChange={(e) => handleChange('is_new_or_used', e.target.value)}
                  className="w-full px-4 py-2 bg-navy-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                >
                  <option value="new">New</option>
                  <option value="used">Used</option>
                  <option value="demo">Demo</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Source</label>
                <select
                  value={formData.source}
                  onChange={(e) => handleChange('source', e.target.value)}
                  className="w-full px-4 py-2 bg-navy-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                >
                  <option value="manual">Manual Entry</option>
                  <option value="referral">Referral</option>
                  <option value="website">Website</option>
                  <option value="phone">Phone Inquiry</option>
                </select>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-navy-800 rounded-xl border border-white/5 p-6">
            <h2 className="text-white font-semibold mb-4">Notes</h2>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={3}
              placeholder="Any additional notes about this deal..."
              className="w-full px-4 py-2 bg-navy-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
            />
          </div>

          {/* Submit */}
          <div className="flex gap-3">
            <Link to="/crm/pipeline" className="flex-1">
              <Button type="button" variant="outline" className="w-full border-gray-600">
                Cancel
              </Button>
            </Link>
            <Button type="submit" disabled={loading} className="flex-1 bg-purple-600 hover:bg-purple-700">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create Deal
            </Button>
          </div>
        </form>
      </div>
    </div>
    </CRMLayout>
  );
}